import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Player, PlayerInTeam } from 'types/players'
import { getPlayers } from 'services/players'
import { PlayerToTeam } from 'types/players-to-teams'
import { addPlayerToTeam, deletePlayerToTeam, editPlayerToTeam, getPlayersToTeam } from 'services/players-to-teams'
import { Form, Radio, Button, Select, Table, Space, Popconfirm, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { getPositions } from 'services/positions'
import { Position } from 'types/positions'
import { modalTitle as formatModalTitle } from 'utils/modal-title'

type Props = {
    teamId: number
    leagueId: number
    teamName?: string
}

const REGULAR = 'REGULAR'
const VICE_CAPTAIN = 'VICE_CAPTAIN'
const CAPTAIN = 'CAPTAIN'

export const PlayersToTeams = memo((props: Props) => {
    const { teamId, leagueId, teamName } = props
    const [form] = Form.useForm()
    const [positions, setPositions] = useState<Position[]>([])
    const [players, setPlayers] = useState<Player[]>([])
    const [playersToTeam, setPlayersToTeam] = useState<PlayerToTeam[]>([])
    const [editablePlayer, setEditablePlayer] = useState<PlayerInTeam>()

    const fetchPlayersToTeam = useCallback(() => {
        getPlayersToTeam(teamId, leagueId).then(setPlayersToTeam)
    }, [leagueId, teamId])

    useEffect(() => {
        getPositions().then(setPositions)
    }, [])

    useEffect(() => {
        getPlayers().then(setPlayers)
    }, [])

    useEffect(() => {
        fetchPlayersToTeam()
    }, [fetchPlayersToTeam, leagueId, teamId])

    const playersInTeam: PlayerInTeam[] = useMemo(
        () =>
            playersToTeam.map(it => {
                return {
                    ...it.player!,
                    viceCaptain: it.viceCaptain ?? 0,
                    captain: it.captain ?? 0,
                    position: it.position!,
                }
            }),
        [playersToTeam]
    )

    const handleAddPlayer = useCallback(
        (data: { playerId: number; positionId: number; captain?: number; viceCaptain?: number }) => {
            const { playerId, positionId, captain, viceCaptain } = data
            addPlayerToTeam({
                playerId,
                teamId,
                leagueId,
                positionId,
                ...{ captain },
                ...{ viceCaptain },
            }).then(() => {
                fetchPlayersToTeam()
                form.resetFields()
            })
        },
        [fetchPlayersToTeam, form, leagueId, teamId]
    )

    const handleEditPlayer = useCallback(
        (data: { playerId: number; positionId: number; captain?: number; viceCaptain?: number }) => {
            const { playerId, positionId, captain, viceCaptain } = data

            editPlayerToTeam(teamId, leagueId, playerId, { positionId, captain, viceCaptain }).then(() => {
                fetchPlayersToTeam()
                form.resetFields()
                setEditablePlayer(undefined)
            })
        },
        [fetchPlayersToTeam, form, leagueId, teamId]
    )

    const handleDeletePlayer = useCallback(
        (playerId: number) => {
            deletePlayerToTeam(teamId, leagueId, playerId).then(fetchPlayersToTeam)
        },
        [fetchPlayersToTeam, leagueId, teamId]
    )

    const handleEdit = useCallback(
        (playerId: number) => {
            const editPlayer = playersInTeam.find(it => it.id === playerId)

            if (editPlayer) {
                setEditablePlayer(editPlayer)

                form.setFieldsValue({
                    role: editPlayer.captain ? CAPTAIN : editPlayer.viceCaptain ? VICE_CAPTAIN : REGULAR,
                    playerId: editPlayer.id,
                    positionId: editPlayer.position?.id,
                })
            }
        },
        [form, playersInTeam]
    )

    const handleCancel = useCallback(() => {
        setEditablePlayer(undefined)
    }, [])

    const onFinish = useCallback(
        (values: { role: string; playerId: number; positionId: number }) => {
            const { playerId, role, positionId } = values

            const action = editablePlayer ? handleEditPlayer : handleAddPlayer

            action({
                playerId,
                positionId,
                viceCaptain: role === VICE_CAPTAIN ? 1 : 0,
                captain: role === CAPTAIN ? 1 : 0,
            })
        },
        [editablePlayer, handleEditPlayer, handleAddPlayer]
    )

    const availablePlayers = useMemo(
        () => players.filter(it => !playersToTeam.find(tt => tt.playerId === it.id)),
        [players, playersToTeam]
    )

    const columns: ColumnsType<PlayerInTeam> = useMemo(
        () => [
            {
                title: 'ID',
                dataIndex: 'id',
                key: 'id',
            },
            {
                title: 'Player name',
                key: 'playerName',
                render: (_, record) => `${record.name} ${record.surname}`,
            },
            {
                title: 'Role',
                key: 'role',
                render: (_, record) => (record.captain ? 'Captain' : record.viceCaptain ? 'Vice captain' : ''),
            },
            {
                title: 'Birthday',
                key: 'birthday',
                render: (_, record) => record.birthday && dayjs(record.birthday).format('DD.MM.YYYY'),
            },
            {
                title: 'Position',
                key: 'positionId',
                render: (_, record) => {
                    return record.position.name
                },
            },
            {
                title: 'Action',
                key: 'action',
                render: (_, record) => {
                    const editMode = !!editablePlayer
                    return (
                        <Space size='middle'>
                            <Typography.Link disabled={editMode} onClick={() => handleEdit(record.id)}>
                                Edit
                            </Typography.Link>
                            <Popconfirm
                                title={formatModalTitle(
                                    'Remove player from team',
                                    teamName
                                        ? `${record.surname} ${record.name} (${teamName})`
                                        : `${record.surname} ${record.name}`
                                )}
                                description={'Are you sure to delete this player from the team?'}
                                onConfirm={() => handleDeletePlayer(record.id)}
                                okText={'Yes'}
                                cancelText={'No'}
                            >
                                <Typography.Link disabled={editMode} href='#'>
                                    Delete
                                </Typography.Link>
                            </Popconfirm>
                        </Space>
                    )
                },
            },
        ],
        [editablePlayer, handleDeletePlayer, handleEdit, teamName]
    )

    return (
        <div>
            <Form form={form} onFinish={onFinish} style={{ border: '1px solid #d2cccc', padding: '10px' }}>
                <Form.Item
                    name={'playerId'}
                    label={'Player'}
                    rules={[{ required: true, message: 'Please select player!' }]}
                >
                    <Select
                        disabled={!!editablePlayer}
                        showSearch
                        options={(availablePlayers || []).map(it => ({
                            value: it.id,
                            label: `${it.surname} ${it.name} (${it.birthday})`,
                        }))}
                        filterOption={(inputValue, option) => {
                            return !!option?.label.toLowerCase().includes(inputValue?.toLowerCase())
                        }}
                    />
                </Form.Item>
                <Form.Item
                    name={'positionId'}
                    label={'Position'}
                    rules={[{ required: true, message: 'Please select player position!' }]}
                >
                    <Select>
                        {positions.map(it => (
                            <Select.Option value={it.id}>{it.name}</Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    label={'Role'}
                    name={'role'}
                    initialValue={REGULAR}
                    rules={[{ required: true, message: 'Please select player role!' }]}
                >
                    <Radio.Group>
                        <Radio.Button value={REGULAR}>Regular</Radio.Button>
                        <Radio.Button value={VICE_CAPTAIN}>Vice captain</Radio.Button>
                        <Radio.Button value={CAPTAIN}>Captain</Radio.Button>
                    </Radio.Group>
                </Form.Item>
                <Form.Item>
                    <Button type={'primary'} htmlType={'submit'}>
                        {editablePlayer ? 'Save' : 'Add'}
                    </Button>
                    <Button htmlType={'reset'} onClick={handleCancel}>
                        {editablePlayer ? 'Cancel' : 'Reset'}
                    </Button>
                </Form.Item>
            </Form>
            <Table columns={columns} dataSource={playersInTeam} rowKey='id' pagination={false} />
        </div>
    )
})
